import { Component, OnInit, OnChanges, AfterViewChecked, Input, Output, EventEmitter, SimpleChanges, ChangeDetectorRef, ElementRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators, AbstractControl } from '@angular/forms';
import { formatDate } from '@angular/common';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { AgrimotorService } from '../agrimotor.service';
import { QuoteService } from '../../quote.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LocalStorageService } from 'ngx-webstorage';
import { ActivatedRoute } from '@angular/router';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';

@Component({
  selector: 'app-ag-proposer-details',
  templateUrl: './proposer-details.component.html',
  styleUrls: ['./proposer-details.component.css', '../../quote.component.css']
})
export class AgrimotorProposerDetailsComponent implements OnInit, OnChanges, AfterViewChecked {
  frmFields: any;
  submitted = false;

  @Input() staticData;
  @Input() modelObj;
  @Input() authDetails;
  @Input() apiModelObj;
  @Input() quote_type: number;
  @Output() modelObjUpdated = new EventEmitter();
  @Output() isFleetActivated = new EventEmitter<boolean>();

  brokers: any = [];
  isBroker: boolean = false;
  isEffectiveDatePast = false;
  minEffectiveDate = new Date();

  addressList: any = [];
  showAddressLookup: boolean = false;

  @ViewChild('agPostcodeAutoTrigger', { read: MatAutocompleteTrigger }) agPostcodeAutoTrigger: MatAutocompleteTrigger;

  minInceptionDate: any = new Date();
  maxInceptionDate: any = new Date();
  maxDOB: any = new Date();

  constructor(
    private fb: FormBuilder,
    private agService: AgrimotorService,
    private quoteService: QuoteService,
    private _snackBar: MatSnackBar,
    private sessionSt: LocalStorageService,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private elRef: ElementRef
  ) { }

  ngOnInit(): void {
    this.frmFields = this.fb.group({
      inception_date: ['', Validators.required],
      effective_date: [''],
      rating_type: ['', Validators.required],
      current_insurer: ['', Validators.required],
      company_id: ['', Validators.required],
      vehicle_counts: this.fb.group({
        agricultural: [0],
        cars: [0],
        commercial: [0],
        horseboxes: [0],
        trailers: [0],
      }),
      proposer: this.buildProposerGroup(),
    });

    this.getBrokers();
    if (this.sessionSt.retrieve('is_broker')) {
      this.isBroker = true;
      this.frmFields.get('company_id').setValue(this.sessionSt.retrieve('broker_company_id'));
    }

    let maxDate = new Date().setDate(new Date().getDate() + 30);
    this.maxInceptionDate = new Date(maxDate);

    let current_date = new Date();
    current_date.setFullYear(new Date().getFullYear() - 16);
    this.maxDOB = current_date.toISOString().slice(0, 10);

    // MTA: enable/disable effective_date based on quote_type
    if (this.quote_type && this.quote_type != 3) {
      this.frmFields.get('effective_date').disable();
    } else if (this.quote_type == 3) {
      this.frmFields.get('effective_date').enable();
      this.frmFields.get('effective_date').setValidators(Validators.required);
    }

    // Auto-lookup the address once the user has finished typing a plausible postcode
    this.frmFields.get('proposer.address.postcode').valueChanges
      .pipe(debounceTime(600), distinctUntilChanged())
      .subscribe((postcode: string) => {
        if (postcode && postcode.replace(/\s/g, '').length >= 5) {
          // Silent while the user is still typing — the Lookup button reports failures.
          this.lookupAddress(false);
        }
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    const application = this.agService.applicationOf(this.apiModelObj);
    if (this.apiModelObj && application) {
      const meta = this.agService.quoteMetaOf(this.apiModelObj);
      this.modelObj = application;
      this.modelObj.id = meta.id;
      this.modelObj.reference = meta.reference;
      this.modelObj.status = meta.status;
      this.modelObj.sequence_number = this.apiModelObj.sequence_number;
      this.modelObj.company_id = this.apiModelObj.company_id;
      if (this.apiModelObj.financial_details) {
        this.modelObj.broker_commission = this.apiModelObj.financial_details.broker_commission;
        this.modelObj.broker_admin_fee = this.apiModelObj.financial_details.broker_admin_fee;
      }
      if (this.apiModelObj.type == 3) {
        this.quote_type = 3;
      }
      if (this.quote_type == 3) {
        let effective_date = new Date(this.modelObj.effective_date);
        let curr_date = new Date();
        if (effective_date < curr_date) {
          this.isEffectiveDatePast = true;
          this.minEffectiveDate = new Date();
        } else {
          this.isEffectiveDatePast = false;
        }
      }
    }
    if (this.frmFields && this.modelObj && this.modelObj.proposer) {
      this.patchValues();
    }
  }

  ngAfterViewChecked() {
    this.cdr.detectChanges();
  }

  get f(): { [key: string]: AbstractControl } {
    if (this.frmFields) {
      return this.frmFields.controls;
    }
  }

  get proposerControls() {
    return ((this.frmFields.get('proposer') as FormGroup).controls);
  }

  // ─── Form Builder ───────────────────────────────────────────────────────────

  buildProposerGroup() {
    return this.fb.group({
      proposer_type: ['', Validators.required],
      micro_enterprise: ['', Validators.required],
      company_name: ['', Validators.required],
      trading_name: [''],
      company_registration_number: [''],
      year_established: ['', Validators.required],
      title: ['', Validators.required],
      first_name: ['', Validators.required],
      last_name: ['', Validators.required],
      dob: ['', Validators.required],
      licence_type: ['', Validators.required],
      licence_length: ['', Validators.required],
      has_convictions: ['', Validators.required],
      convictions: this.fb.array([this.initConviction()]),
      has_claims: ['', Validators.required],
      claims: this.fb.array([this.initClaim()]),
      address: this.fb.group({
        postcode: ['', Validators.required],
        line1: ['', Validators.required],
        line2: [''],
        line3: [''],
        town: [''],
        county: [''],
      }),
      agricultural_trade: ['', Validators.required],
      has_additional_trades: ['', Validators.required],
      contracting_percentage: ['', Validators.required],
      business_use_declaration: [''],
    });
  }

  initConviction() {
    return this.fb.group({
      code: ['', Validators.required],
      date: ['', Validators.required],
      points: ['', Validators.required],
      months_banned: [''],
      fine: ['', Validators.required],
    });
  }

  initClaim() {
    return this.fb.group({
      date: ['', Validators.required],
      incident_type: ['', Validators.required],
      ad_cost: ['', Validators.required],
      tp_cost: ['', Validators.required],
      total_cost: [''],
      at_fault: ['', Validators.required],
      is_open: ['', Validators.required],
    });
  }

  // ─── Patch existing data ─────────────────────────────────────────────────────

  /**
   * The radio groups are 'Yes'/'No' strings, but the API round-trips these answers as real
   * booleans (and sometimes as the strings "true"/"false"). Patching the raw value straight
   * back in left the group with nothing selected, because `true` matches neither radio.
   */
  private toYesNo(val: any): string {
    if (val === true || val === 'Yes' || val === 'true') { return 'Yes'; }
    if (val === false || val === 'No' || val === 'false') { return 'No'; }
    return '';
  }

  /** Entries that are actually filled in — a blank row the user never completed is not an answer. */
  private countPopulated(list: any, isPopulated: (item: any) => any): number {
    if (!Array.isArray(list)) { return 0; }
    return list.filter(item => item && isPopulated(item)).length;
  }

  /**
   * The agri-api does not persist has_convictions / has_claims / has_additional_trades —
   * they are UI-only toggles that reveal the matching section. On reload they come back
   * undefined, and the old `|| ''` fallback left the radio group with no selection at all,
   * so a saved quote reopened blank on all three questions.
   *
   * The list itself is the only surviving source of truth: one or more populated entries
   * means Yes, none means No. ('Yes' always leaves populated entries behind — selecting it
   * enables the FormArray's required validators, so the quote cannot be saved with empty rows.)
   *
   * A quote that has never been saved is left unanswered rather than defaulted to 'No', so the
   * broker still has to make an active choice and the mandatory-field highlighting still fires.
   */
  private yesNoFromCount(count: number): string {
    if (count > 0) { return 'Yes'; }
    return this.modelObj && this.modelObj.id ? 'No' : '';
  }

  private setArrayEnabled(control: AbstractControl, enabled: boolean) {
    if (enabled) { control.enable(); } else { control.disable(); }
  }

  patchValues() {
    // Patch effective_date for MTA
    if (this.quote_type == 3 && this.modelObj.effective_date) {
      this.frmFields.patchValue({ effective_date: new Date(this.modelObj.effective_date) });
      this.frmFields.get('effective_date').enable();
      this.frmFields.get('effective_date').setValidators(Validators.required);
    }
    this.frmFields.patchValue({
      inception_date: this.modelObj.inception_date,
      // Quotes saved before the enum was corrected may still hold 'no_previous'
      rating_type: this.modelObj.rating_type === 'no_previous' ? 'none' : this.modelObj.rating_type,
      current_insurer: this.modelObj.current_insurer,
      company_id: this.modelObj.company_id,
      vehicle_counts: {
        agricultural: this.modelObj.vehicle_counts?.agricultural || 0,
        cars: this.modelObj.vehicle_counts?.cars || 0,
        commercial: this.modelObj.vehicle_counts?.commercial || 0,
        horseboxes: this.modelObj.vehicle_counts?.horseboxes || 0,
        trailers: this.modelObj.vehicle_counts?.trailers || 0,
      },
      
      proposer: {
        proposer_type: this.modelObj.proposer.proposer_type,
        micro_enterprise: this.toYesNo(this.modelObj.proposer.micro_enterprise),
        company_name: this.modelObj.proposer.company_name,
        trading_name: this.modelObj.proposer.trading_name,
        company_registration_number: this.modelObj.proposer.company_registration_number,
        year_established: this.modelObj.proposer.year_established || this.modelObj.proposer.year,
        title: this.modelObj.proposer.title,
        first_name: this.modelObj.proposer.first_name,
        last_name: this.modelObj.proposer.last_name,
        dob: this.modelObj.proposer.dob,
        licence_type: this.modelObj.proposer.licence_type,
        licence_length: this.modelObj.proposer.licence_length,
        has_convictions: this.yesNoFromCount(
          this.countPopulated(this.modelObj.proposer.convictions, (c: any) => c.code && c.date)),
        has_claims: this.yesNoFromCount(
          this.countPopulated(this.modelObj.proposer.claims, (c: any) => c.date && (c.type || c.incident_type))),
        address: {
          postcode: this.modelObj.proposer.address ? this.modelObj.proposer.address.postcode : '',
          line1: this.modelObj.proposer.address ? this.modelObj.proposer.address.line1 : '',
          line2: this.modelObj.proposer.address ? this.modelObj.proposer.address.line2 : '',
          line3: this.modelObj.proposer.address ? this.modelObj.proposer.address.line3 : '',
          town: this.modelObj.proposer.address ? this.modelObj.proposer.address.town : '',
          county: this.modelObj.proposer.address ? this.modelObj.proposer.address.county : '',
        },
        agricultural_trade: this.modelObj.proposer.agricultural_trade || this.modelObj.agricultural_trade,
        has_additional_trades: this.yesNoFromCount(
          this.countPopulated(this.modelObj.additional_trades, (t: any) => !!t)),
        contracting_percentage: this.modelObj.proposer.contracting_percentage || this.modelObj.contracting_percentage,
        business_use_declaration: this.toYesNo(
          this.modelObj.proposer.business_use_declaration !== undefined
            ? this.modelObj.proposer.business_use_declaration
            : this.modelObj.business_use_declaration),
      }
    }, { emitEvent: false });

    // A broker never sees the Broker Details dropdown (*ngIf="!isBroker"), so company_id is
    // filled from their session in ngOnInit. The patch above then overwrote it with
    // modelObj.company_id — '' on a new quote — every time an async @Input (authDetails,
    // staticData) landed and re-ran ngOnChanges. That left a hidden required control invalid,
    // so Proceed only raised the mandatory-field snackbar with nothing on screen to fix.
    // Re-apply it here, as the amline proposer form already does.
    if (this.isBroker) {
      this.frmFields.get('company_id')
        .setValue(this.sessionSt.retrieve('broker_company_id'), { emitEvent: false });
    }

    // Rebuild convictions FormArray
    const convictionsControl = this.getConvictionsArray();
    convictionsControl.clear();
    if (this.modelObj.proposer.convictions && this.modelObj.proposer.convictions.length > 0) {
      this.modelObj.proposer.convictions.forEach(c => {
        convictionsControl.push(this.fb.group({
          code: [c.code, Validators.required],
          date: [c.date, Validators.required],
          points: [c.points, Validators.required],
          months_banned: [c.months_banned],
          fine: [c.fine, Validators.required],
        }));
      });
    } else {
      convictionsControl.push(this.initConviction());
    }

    // Rebuild claims FormArray
    const claimsControl = this.getClaimsArray();
    claimsControl.clear();
    if (this.modelObj.proposer.claims && this.modelObj.proposer.claims.length > 0) {
      this.modelObj.proposer.claims.forEach(c => {
        claimsControl.push(this.fb.group({
          date: [c.date, Validators.required],
          incident_type: [c.incident_type || c.type, Validators.required],
          ad_cost: [c.ad_cost, Validators.required],
          tp_cost: [c.tp_cost, Validators.required],
          total_cost: [c.total_cost !== null && c.total_cost !== undefined ? c.total_cost : ((c.ad_cost || 0) + (c.tp_cost || 0))],
          at_fault: [c.at_fault || (c.fault === true ? 'Yes' : c.fault === false ? 'No' : ''), Validators.required],
          is_open: [c.is_open || (c.open === true ? 'Yes' : c.open === false ? 'No' : ''), Validators.required],
        }));
      });
    } else {
      claimsControl.push(this.initClaim());
    }

    this.setCoverToDate(this.modelObj.inception_date);
    this.disableProposerFields(this.modelObj.proposer.proposer_type);

    // Emit fleet status for existing quotes
    if (this.modelObj.rating_type) {
      this.isFleetActivated.emit(this.modelObj.rating_type === 'fleet');
    }

    // Keep each array's enabled state in step with the Yes/No answer above it. Testing the
    // raw length instead would leave a blank row's required validators active behind a
    // hidden section, blocking Proceed with nothing on screen to explain why.
    //
    // The claims question is asked of companies as well as individuals, but
    // disableProposerFields() only re-enables the array on the individual path — so a
    // company quote reopened on 'Yes' rendered its saved claims greyed out and uneditable.
    this.setArrayEnabled(this.proposerControls.convictions,
      this.proposerControls.has_convictions.value === 'Yes'
        && this.modelObj.proposer.proposer_type === 'individual');
    this.setArrayEnabled(this.proposerControls.claims,
      this.proposerControls.has_claims.value === 'Yes');
  }

  getConvictionsArray(): FormArray {
    return (this.frmFields.get('proposer') as FormGroup).get('convictions') as FormArray;
  }

  getClaimsArray(): FormArray {
    return (this.frmFields.get('proposer') as FormGroup).get('claims') as FormArray;
  }

  // ─── Brokers ─────────────────────────────────────────────────────────────────

  getBrokers() {
    this.quoteService.getBrokers(2)
      .subscribe(
        (response: any) => {
          if (response.success) {
            this.brokers = response.result;
          }
        },
        (error) => { }
      );
  }

  getBrokerCommission(event) {
    let brokerDetails = this.brokers.filter(x => x.id == event.value);
    if (brokerDetails.length > 0) {
      this.modelObj.broker_commission = brokerDetails[0].broker_commission;
      this.modelObj.broker_admin_fee = brokerDetails[0].broker_admin_fee;
    }
  }

  // ─── Cover To Date ───────────────────────────────────────────────────────────

  setCoverToDate(value) {
    if (!value) { return; }
    let maxDate = new Date(value).setFullYear(new Date(value).getFullYear() + 1);
    let coverTo = new Date(new Date(maxDate).getTime() - (24 * 60 * 60 * 1000));
    this.modelObj.cover_to = formatDate(coverTo, 'yyyy-MM-dd', 'en-US');
  }

  onPolicyStartDateChange(event) {
    this.setCoverToDate(event.value);
  }

  // ─── Rating Type ──────────────────────────────────────────────────────────────

  onRatingTypeChange(event) {
    this.isFleetActivated.emit(event.value === 'fleet');
  }

  // ─── Proposer Type ───────────────────────────────────────────────────────────

  onProposerChange(event) {
    this.disableProposerFields(event.value);
  }

  disableProposerFields(proposer_type: string) {
    if (!this.frmFields) { return; }

    this.proposerControls.convictions.disable();
    this.proposerControls.claims.disable();

    if (proposer_type === 'individual') {
      this.proposerControls.title.enable();
      this.proposerControls.first_name.enable();
      this.proposerControls.last_name.enable();
      this.proposerControls.dob.enable();
      this.proposerControls.licence_type.enable();
      this.proposerControls.licence_length.enable();
      this.proposerControls.has_convictions.enable();
      if (this.proposerControls.has_convictions.value === 'Yes') {
        this.proposerControls.convictions.enable();
      }
      this.proposerControls.has_claims.enable();
      if (this.proposerControls.has_claims.value === 'Yes') {
        this.proposerControls.claims.enable();
      }
      this.proposerControls.micro_enterprise.disable();
      this.proposerControls.company_name.disable();
      this.proposerControls.year_established.disable();
      this.proposerControls.business_use_declaration.enable();
    } else if (proposer_type === 'company') {
      this.proposerControls.title.disable();
      this.proposerControls.first_name.disable();
      this.proposerControls.last_name.disable();
      this.proposerControls.dob.disable();
      this.proposerControls.licence_type.disable();
      this.proposerControls.licence_length.disable();
      this.proposerControls.has_convictions.disable();
      this.proposerControls.micro_enterprise.enable();
      this.proposerControls.company_name.enable();
      this.proposerControls.year_established.enable();
      this.proposerControls.business_use_declaration.disable();
      this.proposerControls.business_use_declaration.setValue('');
    }
  }

  // ─── Convictions ─────────────────────────────────────────────────────────────

  onHaveConvictionsChange(event) {
    this.proposerControls.convictions.disable();
    if (event.value === 'Yes') {
      this.proposerControls.convictions.enable();
    }
  }

  addConviction() {
    this.getConvictionsArray().push(this.initConviction());
  }

  removeConviction(index) {
    this.getConvictionsArray().removeAt(index);
  }

  onConvictionCodeChange(conviction: FormGroup) {
    if (this.staticData && this.staticData.conviction_types) {
      let found = this.staticData.conviction_types.find(c => c.code === conviction.get('code').value);
      if (found) {
        conviction.get('description')?.setValue(found.description);
      }
    }
  }

  // ─── Claims ───────────────────────────────────────────────────────────────────

  onHaveClaimsChange(event) {
    this.proposerControls.claims.disable();
    if (event.value === 'Yes') {
      this.proposerControls.claims.enable();
    }
  }

  addClaim() {
    this.getClaimsArray().push(this.initClaim());
  }

  removeClaim(index) {
    this.getClaimsArray().removeAt(index);
  }

  calculateClaimTotal(claim: FormGroup) {
    const ad = claim.get('ad_cost').value || 0;
    const tp = claim.get('tp_cost').value || 0;
    claim.get('total_cost').setValue(ad + tp);
  }

  // ─── Other Trades ─────────────────────────────────────────────────────────────

  onAdditionalTradesChange(event) {
    if (event.value === 'Yes' && (!this.modelObj.additional_trades || this.modelObj.additional_trades.length === 0)) {
      this.addTrade();
    }
  }

  addTrade() {
    if (!this.modelObj.additional_trades) {
      this.modelObj.additional_trades = [];
    }
    if (this.modelObj.additional_trades.length < 10) {
      this.modelObj.additional_trades = [...this.modelObj.additional_trades, ''];
    }
  }

  removeTrade(index) {
    this.modelObj.additional_trades = this.modelObj.additional_trades.filter((_: any, i: number) => i !== index);
  }

  // ─── Address Lookup ───────────────────────────────────────────────────────────
  // Proxied through the backend (avoids CORS and the dead direct getAddress.io
  // 'find' endpoint) — same address/search + address/get pattern used by the
  // amline / excess-protect / breakdown proposer forms.

  isLookingUpAddress: boolean = false;
  addressLookupMessage: string = '';
  /** Stops a slow earlier response from overwriting the results of a newer one. */
  private addressLookupToken = 0;
  /** Set while a picked address is applied, so the postcode write-back cannot reopen the panel. */
  private suppressAddressLookup = false;

  lookupAddress(showFeedback: boolean = true) {
    if (this.suppressAddressLookup) { return; }
    const postcode = (this.frmFields.get('proposer.address.postcode').value || '').trim();
    if (!postcode) {
      if (showFeedback) { this.addressLookupMessage = 'Please enter a postcode to search.'; }
      return;
    }
    this.isLookingUpAddress = true;
    this.addressLookupMessage = '';
    const token = ++this.addressLookupToken;
    this.quoteService.searchAddress(postcode)
      .subscribe(
        (response: any) => {
          if (token !== this.addressLookupToken || this.suppressAddressLookup) { return; }
          this.isLookingUpAddress = false;
          const raw = response?.records || response?.addresses || [];
          const records = Array.isArray(raw) ? raw : [];
          if (records.length > 0) {
            this.addressList = records;
            this.showAddressLookup = true;
            this.addressLookupMessage = '';
            // Options must exist in the DOM before the panel is told to open.
            this.cdr.detectChanges();
            this.agPostcodeAutoTrigger?.openPanel();
          } else {
            this.addressList = [];
            this.showAddressLookup = false;
            this.agPostcodeAutoTrigger?.closePanel();
            // The backend echoes the provider's own message when it has one.
            this.addressLookupMessage = response?.errormessage
              || 'No addresses found for that postcode. Please check it or enter the address manually.';
          }
        },
        () => {
          // Genuine failure (endpoint down / unauthorised) — say so rather than
          // leaving the user staring at a button that appears to do nothing.
          if (token !== this.addressLookupToken) { return; }
          this.isLookingUpAddress = false;
          this.addressList = [];
          this.showAddressLookup = false;
          this.agPostcodeAutoTrigger?.closePanel();
          this.addressLookupMessage = 'The address lookup service is unavailable. Please enter the address manually.';
        }
      );
  }

  selectAddress(address) {
    if (!address) { return; }
    // Close the panel and stall further lookups until the picked address is applied.
    this.suppressAddressLookup = true;
    this.addressLookupToken++;
    this.addressList = [];
    this.agPostcodeAutoTrigger?.closePanel();
    // Keep the postcode the user searched with — the provider's record may format it
    // differently, and it is the field the rest of the quote rates on.
    const searchedPostcode = this.frmFields.get('proposer.address.postcode').value;
    if (address.id != null && address.id !== '') {
      this.quoteService.fetchAddressById(address.id).subscribe(
        (full: any) => {
          this.applyAddressToForm(full);
          const postcode = full?.postcode ?? full?.Postcode ?? searchedPostcode;
          // emitEvent:false — otherwise writing the postcode back re-triggers the
          // debounced auto-lookup and reopens the address picker we just closed.
          this.frmFields.get('proposer.address.postcode')
            .setValue((postcode || '').toUpperCase(), { emitEvent: false });
          this.showAddressLookup = false;
          setTimeout(() => { this.suppressAddressLookup = false; }, 0);
        },
        () => {
          this.suppressAddressLookup = false;
          this.addressLookupMessage = 'Could not retrieve that address. Please enter it manually.';
        }
      );
      return;
    }
    this.applyAddressToForm(address);
    this.showAddressLookup = false;
    setTimeout(() => { this.suppressAddressLookup = false; }, 0);
  }

  private applyAddressToForm(a: any) {
    if (!a) { return; }
    this.frmFields.get('proposer.address.line1').setValue(a.line1 ?? a.line_1 ?? a.Line1 ?? '');
    this.frmFields.get('proposer.address.line2').setValue(a.line2 ?? a.line_2 ?? a.Line2 ?? '');
    this.frmFields.get('proposer.address.line3').setValue(a.line3 ?? a.line_3 ?? a.Line3 ?? '');
    this.frmFields.get('proposer.address.town').setValue(a.town_or_city ?? a.town ?? a.Town ?? '');
    this.frmFields.get('proposer.address.county').setValue(a.county ?? a.County ?? '');
  }

  // ─── Submit ───────────────────────────────────────────────────────────────────

  // Scoped to this component's own DOM — the other (inactive) tabs are eagerly
  // rendered, so a document-wide query would jump to an unrelated hidden control.
  //
  // The `<form>` element itself also carries .ng-invalid whenever the form group is
  // invalid, and it is the FIRST match in document order. Scrolling to it centred the
  // page on the middle of the Proposer form and looked exactly like "Proceed does
  // nothing" — so only real form controls are considered here.
  private scrollToFirstInvalidControl() {
    const candidates: HTMLElement[] = Array.from(
      this.elRef.nativeElement.querySelectorAll('.ng-invalid')
    );
    // First invalid *leaf* in document order: skip the form and any wrapper that merely
    // inherited .ng-invalid from a control inside it. Works for plain inputs and for
    // mat-select alike (a mat-select contains no further .ng-invalid of its own).
    const target = candidates.find(el => el.tagName !== 'FORM' && !el.querySelector('.ng-invalid'));
    if (!target) { return; }
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (typeof target.focus === 'function') { target.focus(); }
  }

  onSubmit() {
    this.submitted = true;
    // Disable effective_date validation when not MTA
    if (this.quote_type != 3) {
      this.frmFields.get('effective_date').disable();
    }
    if (!this.frmFields.valid) {
      this.frmFields.markAllAsTouched();
      this.scrollToFirstInvalidControl();
      this._snackBar.open('Please complete the highlighted mandatory fields before proceeding.', '', {
        duration: 5000,
        panelClass: 'text-danger'
      });
      return false;
    }

    if (this.proposerControls.has_additional_trades.value === 'Yes') {
      const trades = this.modelObj.additional_trades || [];
      if (trades.length === 0 || trades.some((t: string) => !t)) {
        this._snackBar.open('Please select a trade for each additional trade entry.', '', {
          duration: 5000,
          panelClass: 'text-danger'
        });
        return false;
      }
    }

    let formData = this.frmFields.getRawValue();

    this.modelObj.inception_date = formatDate(formData.inception_date, 'yyyy-MM-dd', 'en-US');
    // The agri-api only accepts fleet | ncd | none
    this.modelObj.rating_type = formData.rating_type === 'no_previous' ? 'none' : formData.rating_type;
    this.modelObj.current_insurer = formData.current_insurer;
    this.modelObj.vehicle_counts = formData.vehicle_counts;
    this.modelObj.broker_id = this.sessionSt.retrieve('userInfo').id;
    this.modelObj.declaration = true;
    this.modelObj.agricultural_trade = formData.proposer.agricultural_trade;
    this.modelObj.business_use_declaration = formData.proposer.business_use_declaration === 'Yes' ? true:false;

    if (this.sessionSt.retrieve('company_role') == 'Broker') {
      this.modelObj.company_id = this.sessionSt.retrieve('broker_company_id');
    } else {
      this.modelObj.company_id = formData.company_id;
    }

    let proposer = formData.proposer;
    if (proposer.dob) {
      proposer.dob = formatDate(proposer.dob, 'yyyy-MM-dd', 'en-US');
    }
    if (proposer.licence_length) {
      proposer.licence_length = formatDate(proposer.licence_length, 'yyyy-MM-dd', 'en-US');
    }

    // additional_trades lives at root level (not inside proposer)
    this.modelObj.additional_trades = this.modelObj.additional_trades || [];

    // Clear convictions/claims when user selected "No"
    if (proposer.has_convictions !== 'Yes') {
      proposer.convictions = [];
    }
    if (proposer.has_claims !== 'Yes') {
      proposer.claims = [];
    }

    // The radios start blank (not pre-populated) so the user must make an active
    // choice, but the API expects a definite 'Yes'/'No' — default an untouched
    // radio to 'No' only at submit time, not in the displayed form.
    proposer.has_convictions = proposer.has_convictions || 'No';
    proposer.has_claims = proposer.has_claims || 'No';
    proposer.has_additional_trades = proposer.has_additional_trades || 'No';

    // micro_enterprise is a real boolean in the API. This save previously posted the raw
    // 'Yes'/'No' string and relied on the backend to cast it — which happens to work for
    // 'Yes' but makes 'No' a non-empty string, i.e. truthy. Send the boolean the API asks
    // for, matching what ag-quotation's preparePayload() already does before calculate.
    if (typeof proposer.micro_enterprise === 'string') {
      proposer.micro_enterprise = proposer.micro_enterprise === 'Yes';
    }

    // Map claim fields to API field names
    if (proposer.claims && proposer.claims.length > 0) {
      proposer.claims = proposer.claims.map((c: any) => {
        const mapped: any = { ...c };
        mapped.type = c.incident_type;
        mapped.fault = c.at_fault === 'Yes';
        mapped.open = c.is_open === 'Yes';
        delete mapped.incident_type;
        delete mapped.at_fault;
        delete mapped.is_open;
        return mapped;
      });
    }

    // Map year_established to year for company proposer type
    if (proposer.proposer_type === 'company' && proposer.year_established !== undefined) {
      proposer.year = proposer.year_established;
      delete proposer.year_established;
    }

    this.modelObj.proposer = proposer;
    this.setCoverToDate(this.modelObj.inception_date);

    // MTA: set policy_id, effective_date, old_policy_id, clear id, set type=3
    if (this.quote_type == 3) {
      if (this.apiModelObj && this.apiModelObj.agriculture_data && this.apiModelObj.agriculture_data.data &&
          this.apiModelObj.agriculture_data.data.attributes.application.policy_id) {
        this.modelObj.policy_id = this.apiModelObj.agriculture_data.data.attributes.application.policy_id;
        this.modelObj.effective_date = formatDate(formData.effective_date, 'yyyy-MM-dd', 'en-US');
        this.modelObj.old_policy_id = this.modelObj.id;
        this.modelObj.id = null;
        this.modelObj.type = 3;
      }
    }

    let data = {
      data: {
        type: (this.quote_type == 3) ? 3 : this.modelObj.type,
        attributes: this.modelObj,
        id: this.modelObj.id,
        broker_id: this.modelObj.broker_id,
        sequence_number: this.modelObj.sequence_number,
        old_policy_id: this.modelObj.old_policy_id,
      }
    };

    this.agService.saveQuote(data)
      .subscribe(
        (res: any) => {
          if (res.success) {
            const version = res.result?.quote_data?.quote_versions?.[0];
            const savedApplication = this.agService.applicationOf(version);
            if (version && savedApplication) {
              const meta = this.agService.quoteMetaOf(version);
              // Merge (not replace) — a save response that omits a field must not
              // erase data already entered in another tab (e.g. fleet experience).
              this.modelObj = { ...this.modelObj, ...savedApplication };
              this.modelObj.id = meta.id;
              this.modelObj.broker_id = version.broker_id;
              this.modelObj.reference = meta.reference || res.result.quote_data.reference;
              this.modelObj.status = meta.status;
              this.modelObj.company_id = version.company_id;
              this.modelObj.sequence_number = version.sequence_number;
              if (version.financial_details) {
                this.modelObj.broker_commission = version.financial_details.broker_commission;
                this.modelObj.broker_admin_fee = version.financial_details.broker_admin_fee;
              }
              if (version.policy_holder) {
                // this.modelObj.proposer.email = version.policy_holder.email;
                // this.modelObj.proposer.phone = version.policy_holder.phone;
                // this.modelObj.proposer.is_email = version.policy_holder.is_email;
                // this.modelObj.proposer.is_phone = version.policy_holder.is_phone;
                // this.modelObj.proposer.sms = version.policy_holder.sms;
                // this.modelObj.proposer.post = version.policy_holder.post;
              }
            } else if (res.result && res.result.id) {
              // Fallback: minimal response structure (new quote first save)
              this.modelObj.id = res.result.id;
              if (res.result.sequence_number) {
                this.modelObj.sequence_number = res.result.sequence_number;
              }
            }
            this.modelObjUpdated.emit(this.modelObj);
          } else {
            this._snackBar.open(res.message, '', {
              duration: 5000,
              panelClass: 'text-danger'
            });
          }
        },
        (err) => {
          this._snackBar.open('Error saving quote. Please try again.', '', {
            duration: 5000,
            panelClass: 'text-danger'
          });
        }
      );
  }
}
